/**
 * Secure Token Manager
 * Stores sensitive tokens in memory instead of localStorage
 * This prevents tokens from being visible in browser developer tools
 */

// In-memory token storage (not accessible from browser localStorage)
const tokenStorage = {
  oktaSessionID: null,
  oAuthToken: null,
  idToken: null, // Store id-token in memory for OIDC logout
  storeTokens: {} // For store-specific tokens like RESMED_US_oAuthToken
};

// Secure token management functions
export const setOktaSessionID = (sessionId) => {
  tokenStorage.oktaSessionID = sessionId;
};

export const getOktaSessionID = () => {
  return tokenStorage.oktaSessionID;
};

export const setOAuthToken = (token, storeName) => {
  tokenStorage.oAuthToken = token;
  // Store with store name prefix for compatibility
  if (storeName) {
    tokenStorage.storeTokens[`${storeName}_oAuthToken`] = token;
  }
};

export const getOAuthToken = (storeName = null) => {
  if (storeName) {
    return tokenStorage.storeTokens[`${storeName}_oAuthToken`];
  }
  // If no storeName provided, try to get from current localStorage StoreName
  const currentStoreName = localStorage.getItem('StoreName');
  if (currentStoreName) {
    return tokenStorage.storeTokens[`${currentStoreName}_oAuthToken`];
  }
  return tokenStorage.oAuthToken;
};

// userLoginUsername stays in localStorage - we don't need to manage it in memory
export const setUserLoginUsername = (username) => {
  localStorage.setItem('userLoginUsername', username);
};

export const getUserLoginUsername = () => {
  return localStorage.getItem('userLoginUsername');
};

// ID Token management (in-memory only for security)
export const setIdToken = (token) => {
  tokenStorage.idToken = token;
};

export const getIdToken = () => {
  return tokenStorage.idToken;
};

export const clearAllTokens = () => {
  tokenStorage.oktaSessionID = null;
  tokenStorage.oAuthToken = null;
  tokenStorage.idToken = null;
  tokenStorage.storeTokens = {};
  if (typeof sessionStorage !== 'undefined') {
    // PKCE verifier is persisted in sessionStorage in the redirect flow.
    sessionStorage.removeItem('pkce_code_verifier');
  }
};

/**
 * Convenient functions to replace localStorage calls
 */
export const getSecureOAuthToken = (storeName = null) => {
  return getOAuthToken(storeName);
};

export const getSecureOktaSessionID = () => {
  return getOktaSessionID();
};

// userLoginUsername remains in localStorage - this is just a convenience wrapper
export const getSecureUserLoginUsername = () => {
  return localStorage.getItem('userLoginUsername');
};

/**
 * Clear all tokens from memory (for logout)
 */
export const clearSecureTokens = () => {
  clearAllTokens();
  
  // Clear only the sensitive tokens from localStorage, keep userLoginUsername
  const storeName = localStorage.getItem('StoreName');
  if (storeName) {
    localStorage.removeItem(`${storeName}_oAuthToken`);
  }
  localStorage.removeItem('oktaSessionID');
  // Note: userLoginUsername is intentionally kept in localStorage
};

export default {
  setOktaSessionID,
  getOktaSessionID,
  setOAuthToken,
  getOAuthToken,
  setIdToken,
  getIdToken,
  setUserLoginUsername,
  getUserLoginUsername,
  clearAllTokens,
  getSecureOAuthToken,
  getSecureOktaSessionID,
  getSecureUserLoginUsername,
  clearSecureTokens,
};
